import {Component, ErrorInfo, ReactNode} from 'react'
import {Alert, Button} from 'react-bootstrap'
import {ArrowClockwise, ExclamationTriangle} from 'react-bootstrap-icons'

interface ComponentErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  inline?: boolean;
  errorMessage?: string;
}

interface ComponentErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ComponentErrorBoundary extends Component<ComponentErrorBoundaryProps, ComponentErrorBoundaryState> {
  constructor(props: ComponentErrorBoundaryProps) {
    super(props)
    this.state = {hasError: false, error: null}
  }

  static getDerivedStateFromError(error: Error): ComponentErrorBoundaryState {
    return {hasError: true, error}
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Component error:', error, errorInfo)

    const isChunkError =
      error.message?.includes('dispatcher is null') ||
      error.message?.includes('dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk')

    if (isChunkError) {
      const reloadKey = 'chunk-error-reload'
      const lastReload = sessionStorage.getItem(reloadKey)
      const now = Date.now()

      if (!lastReload || now - parseInt(lastReload) > 10000) {
        sessionStorage.setItem(reloadKey, now.toString())
        window.location.reload()
        return
      }
    }
  }

  handleRetry = (): void => {
    this.setState({hasError: false, error: null})
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const {fallback, inline = false, errorMessage} = this.props

      if (fallback) {
        return fallback
      }

      if (inline) {
        return (
          <Alert
            variant="warning"
            className="d-flex align-items-center gap-2"
          >
            <ExclamationTriangle size={20}/>
            <div className="flex-grow-1">
              {errorMessage || 'Failed to load this section'}
            </div>
            <Button
              size="sm"
              variant="outline-warning"
              onClick={this.handleRetry}
              className="d-flex align-items-center gap-1"
            >
              <ArrowClockwise size={16}/>
              Retry
            </Button>
          </Alert>
        )
      }

      return (
        <div className="p-4 text-center">
          <ExclamationTriangle
            size={32}
            className="text-warning mb-3"
          />
          <p className="text-muted mb-3">
            {errorMessage || 'This component encountered an error'}
          </p>
          <Button
            size="sm"
            variant="primary"
            onClick={this.handleRetry}
            className="d-flex align-items-center gap-2 mx-auto"
          >
            <ArrowClockwise size={18}/>
            Try Again
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}

export default ComponentErrorBoundary
